// src/components/Header/Drawer.jsx
import React from "react";
import {
  alpha,
  Drawer as MuiDrawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  AutoAwesome as WizardIcon,
  Lightbulb as SolutionsIcon,
  Work as WorkspaceIcon,
  School as LearnIcon,
  Event as EventsIcon,
  Person as ProfileIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import "./Drawer.css";
import default_theme from "assets/themes/themes";

const iconMap = {
  "🏠": <HomeIcon />,
  "🧙‍♂️": <WizardIcon />,
  "💡": <SolutionsIcon />,
  "💼": <WorkspaceIcon />,
  "📚": <LearnIcon />,
  "📅": <EventsIcon />,
  "👤": <ProfileIcon />,
  "👥": <PeopleIcon />,
};

const CustomDrawer = ({ open, onToggle, items }) => {
  const location = useLocation();
  const drawerWidth = open ? 240 : 60;

  return (
    <MuiDrawer
      variant="permanent"
      open={open}
      className="custom-drawer"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: default_theme.palette.primary.main,
          color: default_theme.palette.font.gray,
          transition: "width 0.3s ease",
          overflowX: "hidden",
          zIndex: (theme) => theme.zIndex.drawer,
          boxShadow: default_theme.palette.other.drawerShadow,
        },
      }}
    >
      <Box
        className={`drawer-header ${open ? 'open' : 'closed'}`}
        sx={{ justifyContent: open ? 'flex-end' : 'center', width: '100%' }}
      >
        <IconButton
          onClick={onToggle}
          className="drawer-toggle-button"
          sx={{ color: default_theme.palette.primary.green, '&:hover': { backgroundColor: alpha(default_theme.palette.primary.green, 0.1)} }}
        >
          {open ? <ArrowBackIcon /> : <MenuIcon />}
        </IconButton>
      </Box>


      <List className="drawer-list">
        {items.map((item, index) => {
          const isActive = location.pathname === item.link;
          const IconComponent = iconMap[item.icon] || <HomeIcon />;
          const commonSx = {
            backgroundColor: isActive ? alpha(default_theme.palette.primary.green, 0.2): "transparent",
            "&:hover": { backgroundColor: item.comingSoon ? "transparent" : alpha(default_theme.palette.primary.green, 0.1)},
            opacity: item.comingSoon ? 0.5 : 1,
            filter: item.comingSoon ? "grayscale(100%)" : "none",
            cursor: item.comingSoon ? "not-allowed" : "pointer",
            minHeight: 48,
            justifyContent: open ? "initial" : "center",
            px: 2.5,
          };

          const content = (
            <>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                  color: isActive ? default_theme.palette.primary.green : default_theme.palette.primary.gray,
                }}
              >
                {IconComponent}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Clash Display Semibold, sans-serif",
                        color: isActive ? default_theme.palette.primary.green : default_theme.palette.primary.gray,
                        fontWeight: isActive ? "bold" : "normal",
                      }}
                    >
                      {item.text}
                    </Typography>
                    {item.comingSoon && open && (
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: default_theme.palette.other.hoverLight,
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "0.65rem",
                          color: default_theme.palette.primary.gray,
                        }}
                      >
                        Coming Soon
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ opacity: open ? 1 : 0, transition: "opacity 0.3s ease" }}
              />
            </>
          );

          return (
            <Tooltip key={index} title={!open ? item.text : ""} placement="right" arrow>
              {item.comingSoon ? (
                <ListItemButton sx={commonSx} component="div" disabled>
                  {content}
                </ListItemButton>
              ) : (
                <ListItemButton sx={commonSx} component={Link} to={item.link}>
                  {content}
                </ListItemButton>
              )}
            </Tooltip>
          );
        })}
      </List>
    </MuiDrawer>
  );
};

export default CustomDrawer;
